// The class an interactive control inside a graph node must carry (ENG-1121).
//
// WHY A HANDLER IS NOT ENOUGH, because the obvious fix is wrong and was already
// in the code: every one of these controls had `onMouseDown={e =>
// e.stopPropagation()}` and the node still dragged out from under the mouse.
//
// xyflow drags with **d3-drag** (`import { drag } from 'd3-drag'` in
// @xyflow/system), which binds a NATIVE `mousedown.drag` listener on the node
// element. React's `onMouseDown` is a SYNTHETIC event, dispatched at the React
// root after the native event has already propagated through the node element —
// so d3's listener has run and the drag has started before the handler that was
// supposed to prevent it is ever called. `stopPropagation` there stops nothing.
//
// What d3 does honour is xyflow's own opt-out: its `.filter()` refuses the drag
// when `hasSelector(target, '.nodrag', domNode)` matches the event target or any
// ancestor up to the node. Hence a CLASS, not a handler.
//
// `nowheel` is the sibling rule for anything scrollable: without it the wheel
// over a control zooms the canvas instead of scrolling the control.

/** xyflow's drag opt-out. */
export const NODRAG = 'nodrag'
/** xyflow's wheel opt-out — only for controls that consume the wheel. */
export const NOWHEEL = 'nowheel'

/**
 * The class string for a control inside a node.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.scrollable] control consumes the wheel (textarea, a
 *   long select, a scrolling list) — adds `nowheel`.
 * @param {string}  [opts.extra] the component's own classes, kept in front so
 *   its styling hooks read first.
 */
export function nodeControlClass({ scrollable = false, extra = '' } = {}) {
  return [extra.trim(), NODRAG, scrollable ? NOWHEEL : ''].filter(Boolean).join(' ')
}

/**
 * Whether a control's class string already opts out of the node drag. Used by
 * the ratchet test so the check and the components cannot drift.
 */
export function optsOutOfNodeDrag(className) {
  return String(className ?? '').split(/\s+/).includes(NODRAG)
}

/**
 * Parse a typed value for a numeric node field.
 *
 * Deliberately NOT `input[type=number]`: its spinner auto-repeats while held and
 * stops on mouseup, and a node re-rendering on the editor's 100ms debug poll can
 * lose that mouseup — which reads as the value running away on its own
 * (RigChainNodes' ValueRow carries the original note). A text field with
 * `inputMode="decimal"` takes a typed value with no spinner at all.
 *
 * Returns null for anything unparseable, so the caller keeps the old value
 * rather than writing NaN into the graph.
 */
export function parseNodeNumber(raw, { min = -Infinity, max = Infinity } = {}) {
  const text = String(raw ?? '').trim()
  // `Number('')` and `Number('   ')` are BOTH 0, so an emptied field would
  // silently write zero over the value instead of leaving it alone. Empty is
  // "no value typed", not "zero".
  if (text === '') return null
  const n = Number(text)
  if (!Number.isFinite(n)) return null
  return Math.min(max, Math.max(min, n))
}
